import { useEffect, useRef, useState } from 'react';

import type { Theme } from '@material-ui/core';
import { IconButton, makeStyles, useTheme } from '@material-ui/core';

import { RiFullscreenLine } from '@remixicon/react';
import type { Loader } from 'vega';
import type { Config, Result } from 'vega-embed';

import {
  defaultChartColors,
  getChartColors,
} from '@spotify/confidence-charts-react';
import { DialogComposite, useDisclosure } from '@spotify/confidence-core-react';

import { injectCompactAxisFormat } from './vegaSpecUtils';

const sandboxLoader: Loader = {
  load: () => Promise.reject(new Error('External data loading is disabled')),
  http: () => Promise.reject(new Error('External data loading is disabled')),
  file: () => Promise.reject(new Error('External data loading is disabled')),
  sanitize: (uri: string) => Promise.resolve({ href: uri, localFile: false }),
};

function stripDataUrls(spec: Record<string, unknown>) {
  const stripFromArray = (arr: unknown[]) => {
    for (const entry of arr) {
      if (typeof entry === 'object' && entry !== null && 'url' in entry) {
        delete (entry as Record<string, unknown>).url;
      }
    }
  };
  if (Array.isArray(spec.data)) {
    stripFromArray(spec.data);
  } else if (
    typeof spec.data === 'object' &&
    spec.data !== null &&
    'url' in spec.data
  ) {
    delete (spec.data as Record<string, unknown>).url;
  }
  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer === 'object' && layer !== null) {
        stripDataUrls(layer as Record<string, unknown>);
      }
    }
  }
}

function buildVegaConfig(theme: Theme): Config {
  const isDark = theme.palette.type === 'dark';
  const bg = theme.palette.background.default;
  const textColor = theme.palette.text.secondary;
  const titleColor = theme.palette.text.primary;
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.1)';
  const fontFamily = theme.typography.fontFamily as string;
  const colors = getChartColors(isDark ? 'dark' : 'light');
  const palette = defaultChartColors.map(name => colors[name]);

  return {
    background: bg,
    title: {
      color: titleColor,
      subtitleColor: textColor,
      font: fontFamily,
      subtitleFont: fontFamily,
      fontSize: 14,
      fontWeight: 500,
    },
    axis: {
      labelColor: textColor,
      titleColor: textColor,
      gridColor,
      domainColor: gridColor,
      tickColor: gridColor,
      labelFont: fontFamily,
      titleFont: fontFamily,
      labelFontSize: 11,
      titleFontSize: 12,
    },
    legend: {
      labelColor: textColor,
      titleColor: textColor,
      labelFont: fontFamily,
      titleFont: fontFamily,
      labelFontSize: 11,
    },
    range: {
      category: palette,
      ordinal: { scheme: palette },
    },
    mark: { color: palette[0] },
    bar: { color: palette[0] },
    line: { color: palette[0], strokeWidth: 2 },
    area: { color: palette[0], opacity: 0.6 },
    point: { color: palette[0], size: 40 },
    text: { color: textColor },
    view: { stroke: 'transparent' },
  };
}

function formatTooltip(
  value: Record<string, unknown>,
  valueToHtml: (value: unknown) => string,
): string {
  return Object.entries(value)
    .map(
      ([key, val]) =>
        `<div style="margin-bottom:2px"><strong>${key}:</strong> ${valueToHtml(val)}</div>`,
    )
    .join('');
}

// Line/area marks are single SVG paths — add point dots as hover targets
function injectTooltipInteractivity(spec: Record<string, unknown>): void {
  try {
    const isVegaLite =
      typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');
    if (!isVegaLite) return;

    const enableMarkTooltip = (markValue: unknown): unknown => {
      if (typeof markValue === 'string') {
        const t = markValue as string;
        if (t === 'line' || t === 'area') {
          return { type: t, tooltip: true, point: true };
        }
        return { type: t, tooltip: true };
      }
      if (typeof markValue === 'object' && markValue !== null) {
        const m = markValue as Record<string, unknown>;
        if (m.tooltip === undefined) m.tooltip = true;
        if ((m.type === 'line' || m.type === 'area') && m.point === undefined) {
          m.point = true;
        }
        return m;
      }
      return markValue;
    };

    // Handle layered specs
    if (Array.isArray(spec.layer)) {
      for (const layer of spec.layer) {
        if (typeof layer !== 'object' || layer === null) continue;
        const l = layer as Record<string, unknown>;
        if (l.mark !== undefined) {
          l.mark = enableMarkTooltip(l.mark);
        }
      }
      return;
    }

    // Non-layered spec
    if (spec.mark !== undefined) {
      spec.mark = enableMarkTooltip(spec.mark);
    }
  } catch {
    // Defensive — don't crash on unexpected spec shapes
  }
}

const CHART_H_PADDING = 72;
const DEFAULT_SPEC_WIDTH = 500;
const DEFAULT_SPEC_HEIGHT = 400;

function applyResponsiveSize(
  spec: Record<string, unknown>,
  availableWidth: number,
  theme: Theme,
) {
  const isVegaLite =
    typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');

  if (isVegaLite && availableWidth > 0) {
    spec.width = availableWidth;
    return;
  }

  if (availableWidth <= 0) return;

  const specPadding = spec.padding;
  let hPadding = 0;
  if (typeof specPadding === 'number') {
    hPadding = specPadding * 2;
  } else if (typeof specPadding === 'object' && specPadding !== null) {
    const p = specPadding as Record<string, number>;
    hPadding = (p.left ?? 0) + (p.right ?? 0);
  }
  const totalWidth = ((spec.width as number) ?? DEFAULT_SPEC_WIDTH) + hPadding;
  if (totalWidth > availableWidth) {
    const scale = availableWidth / totalWidth;
    spec.width = Math.round(
      ((spec.width as number) ?? DEFAULT_SPEC_WIDTH) * scale,
    );
    spec.height = Math.round(
      ((spec.height as number) ?? DEFAULT_SPEC_HEIGHT) * scale,
    );
    if (typeof specPadding === 'number') {
      spec.padding = Math.round(specPadding * scale);
    } else if (typeof specPadding === 'object' && specPadding !== null) {
      const p = specPadding as Record<string, number>;
      spec.padding = {
        left: Math.round((p.left ?? 0) * scale),
        right: Math.round((p.right ?? 0) * scale),
        top: Math.round((p.top ?? 0) * scale),
        bottom: Math.round((p.bottom ?? 0) * scale),
      };
    }
  }

  // Inject text color for full Vega text marks
  const textColor = theme.palette.text.secondary;
  if (Array.isArray(spec.marks)) {
    for (const mark of spec.marks as Record<string, unknown>[]) {
      const encode = mark.encode as
        | Record<string, Record<string, unknown>>
        | undefined;
      if (mark.type === 'text' && encode?.enter) {
        encode.enter.fill = encode.enter.fill ?? { value: textColor };
      }
    }
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    '&:hover $expandButton, &:focus-within $expandButton': {
      opacity: 1,
    },
  },
  chartContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4, 0),
    overflow: 'hidden',
    textAlign: 'center',
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
  expandButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dialogBody: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: theme.spacing(2),
  },
  dialogChartContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    width: '100%',
    textAlign: 'center',
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
}));

type VegaChartProps = {
  content: string;
  className?: string;
};

const VegaChart = ({ content, className }: VegaChartProps) => {
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!rootRef.current) return undefined;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !content || containerWidth === 0) {
      return undefined;
    }

    let result: Result | null = null;

    const renderChart = async () => {
      try {
        const { default: vegaEmbed } = await import('vega-embed');
        const spec = JSON.parse(content);
        const availableWidth =
          containerWidth > 0 ? containerWidth - CHART_H_PADDING : 0;

        applyResponsiveSize(spec, availableWidth, theme);
        stripDataUrls(spec);
        injectTooltipInteractivity(spec);

        const isVegaLite =
          typeof spec.$schema === 'string' &&
          spec.$schema.includes('vega-lite');
        if (isVegaLite) {
          injectCompactAxisFormat(spec);
        }

        result = await vegaEmbed(containerRef.current!, spec, {
          actions: false,
          renderer: 'svg',
          config: buildVegaConfig(theme),
          loader: sandboxLoader,
          tooltip: { formatTooltip },
        });
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to render chart');
      }
    };

    renderChart();
    return () => {
      result?.finalize();
    };
  }, [content, theme, containerWidth]);

  if (error) return null;

  return (
    <div ref={rootRef} style={{ width: '100%' }}>
      <div ref={containerRef} className={className} />
    </div>
  );
};

export type ChartBlockProps = {
  content: string;
};

export const ChartBlock = ({ content }: ChartBlockProps) => {
  const classes = useStyles();
  const [dialogOpen, dialog] = useDisclosure();

  return (
    <>
      <div className={classes.root}>
        <VegaChart content={content} className={classes.chartContainer} />
        <IconButton
          className={classes.expandButton}
          size="small"
          onClick={dialog.open}
          aria-label="Expand chart"
        >
          <RiFullscreenLine size={18} />
        </IconButton>
      </div>

      {dialogOpen && (
        <DialogComposite.Root
          open={dialogOpen}
          onClose={dialog.close}
          width="xl"
        >
          <DialogComposite.Content>
            <DialogComposite.Header>
              <DialogComposite.Title>Chart</DialogComposite.Title>
              <DialogComposite.CloseButton />
            </DialogComposite.Header>
            <DialogComposite.Body className={classes.dialogBody} autoHeight>
              <VegaChart
                content={content}
                className={classes.dialogChartContainer}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      )}
    </>
  );
};
